import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, Calendar, MessageSquare, CheckCircle, Loader2, PhoneCall } from 'lucide-react';
import api from '../services/api';

// The dashboard's ONE callbacks surface (Client-Call Discernment v1). Fed by
// GET /api/callbacks — the unified queue written by three sources (a taken
// message, a booked callback, a client Sigil message). Ordered hot -> scheduled
// -> unscheduled by the API.
const SOURCE_META = {
  call_message: { label: 'Message', Icon: MessageSquare, cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  call_booking: { label: 'Scheduled call', Icon: Calendar, cls: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' },
  sigil: { label: 'Portal message', Icon: MessageSquare, cls: 'bg-violet-500/20 text-violet-300 border-violet-500/40' },
  sms_reply: { label: 'Text reply', Icon: MessageSquare, cls: 'bg-teal-500/20 text-teal-300 border-teal-500/40' },
};

const fmtWhen = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

const CallbacksCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const res = await api.get('/callbacks'); setItems(res.data?.items || []); }
    catch (e) { /* best-effort: keep prior items */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markDone = async (item) => {
    setBusy(`done:${item.id}`);
    try { await api.post(`/callbacks/${item.id}/done`); setItems((p) => p.filter((i) => i.id !== item.id)); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const haveAstraeaCall = async (item) => {
    if (!item.contact_id) { alert('No contact linked to this item.'); return; }
    setBusy(`call:${item.id}`);
    try {
      await api.post(`/contacts/${item.contact_id}/clarissa-call`);
      alert('Astraea will call to schedule. (Respects the duplicate guard — if they are already booked, it will not dial.)');
    } catch (e) { alert(e.response?.data?.detail || 'Failed to queue the call'); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl p-6 border border-virgo-teal-700/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-teal-500/20 rounded-lg"><PhoneCall className="w-5 h-5 text-teal-400" /></div>
        <div>
          <h3 className="text-lg font-semibold text-white">Callbacks · {items.length}</h3>
          <p className="text-xs text-virgo-navy-300">Who's waiting to hear back — hot first</p>
        </div>
        {items.length > 0 && <span className="ml-auto text-sm text-teal-300">{items.length}</span>}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-virgo-navy-300 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-virgo-navy-300 py-4 text-center">No one waiting on a callback.</div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {/* B3: queues sort OLDEST-first — age is debt. */}
          {[...items].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)).map((item) => {
            const meta = SOURCE_META[item.source] || SOURCE_META.call_message;
            const Icon = meta.Icon;
            const when = fmtWhen(item.scheduled_at);
            return (
              <div key={item.id} className={`rounded-lg p-3 border ${item.hot ? 'border-red-500/50 bg-red-500/5' : 'border-virgo-navy-600 bg-virgo-navy-800/50'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${meta.cls} whitespace-nowrap`}><Icon className="w-3 h-3 inline mr-1" />{meta.label}</span>
                  {item.hot && <span className="text-[10px] px-2 py-0.5 rounded border border-red-500/50 text-red-300 bg-red-500/10">HOT</span>}
                  {when
                    ? <span className="text-[10px] text-cyan-300 flex items-center gap-1 ml-auto"><Clock className="w-3 h-3" />{when}</span>
                    : <span className="text-[10px] text-virgo-navy-400 ml-auto">unscheduled</span>}
                </div>
                <div className="mt-1.5">
                  <div className="text-sm font-medium text-white">{item.contact_name || 'Unknown caller'}{item.case_number ? <span className="text-virgo-navy-400 font-normal"> · #{item.case_number}</span> : null}</div>
                  {item.body && <div className="text-xs text-virgo-navy-200 mt-0.5" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.body}</div>}
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {((item.source === 'sms_reply' && item.thread_id) || (item.source === 'sigil' && item.contact_id)) && (
                    <Link to={`/sigil?thread=${item.source === 'sms_reply' ? item.thread_id : item.contact_id}`} className="text-xs px-2.5 py-1 rounded bg-teal-500/20 text-teal-300 border border-teal-500/40 flex items-center gap-1 hover:bg-teal-500/30">
                      <MessageSquare className="w-3 h-3" />Open conversation
                    </Link>
                  )}
                  {item.phone && <a href={`tel:${item.phone}`} className="text-xs px-2.5 py-1 rounded bg-teal-500/20 text-teal-300 border border-teal-500/40 flex items-center gap-1 hover:bg-teal-500/30"><Phone className="w-3 h-3" />Call</a>}
                  {!item.scheduled_at && item.contact_id && (
                    <button disabled={busy === `call:${item.id}`} onClick={() => haveAstraeaCall(item)} className="text-xs px-2.5 py-1 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex items-center gap-1 hover:bg-cyan-500/30 disabled:opacity-50">
                      {busy === `call:${item.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <PhoneCall className="w-3 h-3" />}Have Astraea call to schedule
                    </button>
                  )}
                  <button disabled={busy === `done:${item.id}`} onClick={() => markDone(item)} className="text-xs px-2.5 py-1 rounded bg-virgo-navy-700 text-virgo-navy-200 border border-virgo-navy-600 flex items-center gap-1 hover:bg-virgo-navy-600 ml-auto disabled:opacity-50">
                    {busy === `done:${item.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}Done
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CallbacksCard;
